import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/authContext';

const TrainingModules = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [howToProgress, setHowToProgress] = useState(null);

  useEffect(() => {
    if (currentUser?.uid) {
      loadProgress();
    }
  }, [currentUser]);

  const loadProgress = async () => {
    try {
      const ref = doc(db, 'users', currentUser.uid, 'trainingProgress', 'how-to-use-tom');
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setHowToProgress(snap.data());
      }
    } catch (error) {
      console.error('Error loading training progress:', error);
    }
  };

  const howToCompleted = howToProgress?.completed || false;
  const howToStepsCompleted = howToProgress?.completedSteps
    ? Object.values(howToProgress.completedSteps).filter(Boolean).length
    : 0;

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Training Modules
        </h1>
        <p className="text-gray-600 mb-4">
          Select a training module to begin your learning journey
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* How to Use TOM Card */}
        <div
          onClick={() => navigate('/how-to-use-tom')}
          className={`bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer relative overflow-hidden ${
            howToCompleted ? 'ring-2 ring-[#5ABA47]/30' : ''
          }`}
        >
          {howToCompleted && (
            <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-[#5ABA47] flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          )}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-[#0076BE] bg-[#0076BE]/10 px-2 py-0.5 rounded-full">
                Recommended
              </span>
              <span className="text-xs text-gray-400">3 steps</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">How to Use TOM</h2>
            <p className="text-gray-600 text-sm">
              Learn how to add disciples, send meaningful messages, and build a rhythm of discipleship using Together On Mission.
            </p>
            {/* Progress bar */}
            <div className="mt-4 mb-2">
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#5ABA47] rounded-full transition-all duration-500"
                  style={{ width: `${(howToStepsCompleted / 3) * 100}%` }}
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400">{howToStepsCompleted}/3 complete</span>
              <div className="flex items-center text-[#0076BE] text-sm font-medium">
                <span>{howToCompleted ? 'Review' : howToStepsCompleted > 0 ? 'Continue' : 'Start'}</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Biscuit Trail Card */}
        <div 
          onClick={() => navigate('/biscuit-trail')}
          className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
        >
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">The Biscuit Trail</h2>
            <p className="text-gray-600 text-sm">
              Learn how to effectively share your faith by arranging key discipleship steps in the most impactful order.
            </p>
            <div className="mt-4 flex items-center text-[#5ABA47] text-sm font-medium">
              <span>Start Module</span>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
        </div>

        {/* Placeholder for future modules */}
        <div className="bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 p-6 flex items-center justify-center">
          <p className="text-gray-500 text-center text-sm">More training modules coming soon...</p>
        </div>
      </div>
    </div>
  );
};

export default TrainingModules; 